import { createBrowserRouter } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Root from './components/Root';
import NotFound from './routes/NotFound';
import Login from './routes/member/Login';
import { nlPost } from './apis/notLoginApi';

import ExpertAnalyze from './routes/expert/analyze/ExpertAnalyze';
import ExpertAnalyzeDetail from './routes/expert/analyze/ExpertAnalyzeDetail';
import ExpertConsult from './routes/expert/consult/ExpertConsult';
import ExpertConsultDetail from './routes/expert/consult/ExpertConsultDetail';
import ExpertList from './routes/expert/register/ExpertList';
import ExpertRegister from './routes/expert/register/ExpertRegister';

import Dashboard from './routes/main/Dashboard';

import Process from './routes/aia/Process';
import Task from './routes/aia/Task';
import SurveyList from './routes/aia/SurveyList';
import WrkrList from './routes/aia/WrkrList';
import SymptomList from './routes/aia/SymptomList';
import SurveySymptom from './routes/aia/SurveySymptom';
import SurveySymptomCmplt from './routes/aia/SurveySymptomCmplt';
import SurveySymptomResult from './routes/aia/SurveySymptomResult';
import SurveyCheck from './routes/aia/SurveyCheck';
import CheckList from './routes/aia/CheckList';
import JoinTerms from './routes/member/JoinTerms';
import JoinTermsEnt from './routes/member/JoinTermsEnt';
import BasicSurveyFirst from './routes/aia/BasicSurveyFirst';
import BasicSurveySecond from './routes/aia/BasicSurveySecond';
import ResVideoList from './routes/aia/ResVideoList';
import ResVideoDetail from './routes/aia/ResVideoDetail';
import ResVideoFrame from './routes/aia/ResVideoFrame';
import Guide from './routes/aia/Guide';

import EntJoinBRNO from './routes/member/EntJoinBRNO';
import JoinPage from './routes/member/JoinPage';
import EntJoin from './routes/member/EntJoin';
import ExptJoin from './routes/member/ExptJoin';
import EntInfo from './routes/member/EntInfo';
import UserJoinCmplt from './routes/member/UserJoinCmplt';
import JoinEmlChk from './routes/member/JoinEmlChk';
import UserJoin from './routes/member/UserJoin';
import UserInfo from './routes/member/UserInfo';
import UserInfoPswdChk from './routes/member/UserInfoPswdChk';
import UserWithdrawal from './routes/member/UserWithdrawal';
import EntWithdrawal from './routes/member/EntWithdrawal';
import FaqList from './routes/cs/FaqList';
import QuestionList from './routes/cs/QuestionList';
import AnsQuestion from './routes/cs/AnsQuestion';
import NoticeList from './routes/cs/NoticeList';
import FindId from './routes/member/FindId';
import FindPassword from './routes/member/FindPassword';
import ChangePassword from './routes/member/ChangePassword';
import JoinCmplt from './routes/member/JoinCmplt';
import RegQuestion from './routes/cs/RegQuestion';
import ReqVideo from './routes/aia/ReqVideo';

import PerUser from './routes/stats/entStats/PerUser';
import PerSizeAndType from './routes/stats/entStats/PerSizeAndType';
import AccessLog from './routes/stats/AccessLog';
import TaskStats from './routes/stats/TaskStats';
import ReqStats from './routes/stats/ReqStats';
import ResStats from './routes/stats/ResStats';
import ResScoreStats from './routes/stats/ResScoreStats';
import ExptReqStats from './routes/stats/ExptReqStats';
import ExptResStats from './routes/stats/ExptResStats';
import ImpvEnv from './routes/stats/ImpvEnv';
import AdminPI from './routes/member/AdminPI';
import AdminTOS from './routes/member/AdminTOS';
import AdminRefund from './routes/member/AdminRefund';

import SelectSub from './routes/pay/SelectSub';
import SubList from './routes/pay/SubList';
import PayList from './routes/pay/PayList';
import PayMethod from './routes/pay/PayMethod';

import AdminEntList from './routes/admin/AdminEntList';
import AdminEntInfo from './routes/admin/AdminEntInfo';
import AdminLogin from './routes/admin/AdminLogin';
import AdminUserList from './routes/admin/AdminUserList';
import AdminUserInfo from './routes/admin/AdminUserInfo';
import AdminInfo from './routes/admin/AdminInfo';
import AllowIp from './routes/system/AllowIp';
import ArlmManage from './routes/system/ArlmManage';
import ErrorPage from './routes/errorPage/ErrorPage';
import ReportList from './routes/report/ReportList';

import Sample from './routes/Sample';

async function checkIp() {
  try {
    const response = await nlPost('/api/check-ip', {});

    if (response?.allowed !== undefined) {
      return response.allowed;
    }
    return false;
  } catch (error) {
    return false;
  }
}

function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    checkIp()
      .then(isAllowed => {
        setAllowed(isAllowed);

        if (!isAllowed) {
          navigate('/');
        }
      })
      .catch(() => {
        setAllowed(false);
        navigate('/');
      });
  }, [navigate]);

  if (allowed === null) {
    return <div>Loading...</div>;
  }

  return allowed ? children : null;
}

const ROUTER_FLAGS = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
  v7_fetcherPersist: true,
  v7_normalizeFormMethod: true,
  v7_skipActionErrorRevalidation: true,
  v7_partialHydration: true,
  v7_prependBasename: true,
};

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <Root />,
      errorElement: <NotFound />,
      children: [
        {
          path: '',
          element: <Dashboard />,
        },
        {
          path: 'member',
          children: [
            {
              path: 'login',
              element: <Login />,
            },
            // 회원가입 선택 - ALL
            {
              path: 'joinPage',
              element: <JoinPage />,
            },
            // 중복체크 - 기업회원
            {
              path: 'entJoinBRNO',
              element: <EntJoinBRNO />,
            },
            // 중복체크 - 일반회원, 전문가
            {
              path: 'joinEmlChk',
              element: <JoinEmlChk />,
            },
            // 이용약관 - 기업회원, 전문가
            {
              path: 'joinTermsEnt',
              element: <JoinTermsEnt />,
            },
            // 이용약관 - 일반회원
            {
              path: 'joinTerms',
              element: <JoinTerms />,
            },
            // 정보입력 - 기업회원
            {
              path: 'joinEnt',
              element: <EntJoin />,
            },
            // 정보입력 - 일반회원
            {
              path: 'joinUser',
              element: <UserJoin />,
            },
            // 정보입력 - 전문가
            {
              path: 'joinExpt',
              element: <ExptJoin />,
            },
            // 회원가입완료 - 기업회원, 일반회원
            {
              path: 'joinCmplt',
              element: <JoinCmplt />,
            },
            // 회원가입완료 - 전문가
            {
              path: 'userJoinCmplt',
              element: <UserJoinCmplt />,
            },
            {
              path: 'findId',
              element: <FindId />,
            },
            {
              path: 'findPassword',
              element: <FindPassword />,
            },

            {
              path: 'userInfo',
              children: [
                {
                  path: 'entInfo',
                  element: <EntInfo />,
                },
                {
                  path: 'userInfo',
                  element: <UserInfo />,
                },
                {
                  path: 'userInfoPswdChk',
                  element: <UserInfoPswdChk />,
                },
                {
                  path: 'chgPassword',
                  element: <ChangePassword />,
                },
                {
                  path: 'userWithdrawal',
                  element: <UserWithdrawal />,
                },
                {
                  path: 'entWithdrawal',
                  element: <EntWithdrawal />,
                },
                {
                  path: 'adminEntInfo',
                  element: <AdminEntInfo />,
                },
                {
                  path: 'adminUserInfo',
                  element: <AdminUserInfo />,
                },
                {
                  path: 'adminInfo',
                  element: <AdminInfo />,
                },
              ],
            },
            {
              path: 'adminUser',
              children: [
                {
                  path: 'adminEntUser',
                  element: <AdminEntList />,
                },
                {
                  path: 'adminPersonalUser',
                  element: <AdminUserList />,
                },
              ],
            },
            {
              path: 'adminPI',
              element: <AdminPI />,
            },
            {
              path: 'adminTOS',
              element: <AdminTOS />,
            },
            {
              path: 'adminRefund',
              element: <AdminRefund />,
            },
          ],
        },
        {
          path: 'cs',
          children: [
            {
              path: 'faqList',
              element: <FaqList />,
            },
            {
              path: 'question',
              children: [
                {
                  path: 'questionList',
                  element: <QuestionList />,
                },
                {
                  path: 'regQuestion',
                  element: <RegQuestion />,
                },
                {
                  path: 'ansQuestion',
                  element: <AnsQuestion />,
                },
              ],
            },
            {
              path: 'noticeList',
              element: <NoticeList />,
            },
          ],
        },
        {
          path: 'pay',
          children: [
            {
              path: 'selectSub',
              element: <SelectSub />,
            },
            {
              path: 'subList',
              element: <SubList />,
            },
            {
              path: 'payList',
              element: <PayList />,
            },
            {
              path: 'payMethod',
              element: <PayMethod />,
            },
          ],
        },
        {
          path: 'expert',
          children: [
            {
              path: 'register',
              children: [
                {
                  path: 'list',
                  element: <ExpertList />,
                },
                {
                  path: 'register',
                  element: <ExpertRegister />,
                },
              ],
            },
            {
              path: 'analyze',
              children: [
                {
                  path: 'list',
                  element: <ExpertAnalyze />,
                },
                {
                  path: 'detail',
                  element: <ExpertAnalyzeDetail />,
                },
              ],
            },
            {
              path: 'consult',
              children: [
                {
                  path: 'list',
                  element: <ExpertConsult />,
                },
                {
                  path: 'detail',
                  element: <ExpertConsultDetail />,
                },
              ],
            },
          ],
        },
        {
          path: 'ai',
          children: [
            {
              path: 'guide',
              element: <Guide />,
            },
            {
              path: 'process',
              element: <Process />,
            },
            {
              path: 'task',
              element: <Task />,
            },
            {
              path: 'surveyList',
              element: <SurveyList />,
            },
            {
              path: 'surveyCheck',
              element: <CheckList />,
            },
            {
              path: 'reqVideo',
              element: <ReqVideo />,
            },
            {
              path: 'resVideoList',
              element: <ResVideoList />,
            },
            {
              path: 'resVideoDetail',
              element: <ResVideoDetail />,
            },
            {
              path: 'basicSurveyFirst',
              element: <BasicSurveyFirst />,
            },
            {
              path: 'basicSurveySecond',
              element: <BasicSurveySecond />,
            },
            {
              path: 'wrkrList',
              element: <WrkrList />,
            },
            {
              path: 'symptomList',
              element: <SymptomList />,
            },
            {
              path: 'surveySymptom',
              element: <SurveySymptom />,
            },
            {
              path: 'surveySymptomResult',
              element: <SurveySymptomResult />,
            },
            {
              path: 'surveySymptomCmplt',
              element: <SurveySymptomCmplt />,
            },
            {
              path: 'ResVideoFrame',
              element: <ResVideoFrame />,
            },
          ],
        },
        {
          path: 'stats',
          children: [
            {
              path: 'entStats',
              children: [
                {
                  path: 'perUser',
                  element: <PerUser />,
                },
                {
                  path: 'perSizeAndType',
                  element: <PerSizeAndType />,
                },
              ],
            },
            {
              path: 'taskStats',
              element: <TaskStats />,
            },
            {
              path: 'accessLog',
              element: <AccessLog />,
            },
            {
              path: 'reqStats',
              element: <ReqStats />,
            },
            {
              path: 'resStats',
              element: <ResStats />,
            },
            {
              path: 'resScoreStats',
              element: <ResScoreStats />,
            },
            {
              path: 'exptReqStats',
              element: <ExptReqStats />,
            },
            {
              path: 'exptResStats',
              element: <ExptResStats />,
            },
            {
              path: 'impvEnv',
              element: <ImpvEnv />,
            },
          ],
        },
        {
          path: 'system',
          children: [
            {
              path: 'alrm',
              element: <ArlmManage />,
            },
            {
              path: 'allowIP',
              element: <AllowIp />,
            },
            {
              // path: '%i.n%o.d%m.g%i.n%l.a',
              path: 'admin',
              element: (
                <ProtectedRoute>
                  <AdminLogin />
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: 'report',
          children: [
            {
              path: 'list',
              element: <ReportList />,
            },
          ],
        },
        {
          path: '/sample',
          element: <Sample />,
        },
      ],
    },
    {
      path: '/error',
      element: <ErrorPage />,
    },
  ],
  {
    future: ROUTER_FLAGS,
    basename: '/', // basename 명시적 설정
  }
);

export default router;
